import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=authenticated
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Fallback per sessioni esistenti: se non c'è timestamp lo settiamo ora
      if (!localStorage.getItem("session_started_at")) {
        localStorage.setItem("session_started_at", String(Date.now()));
      }
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // 🆕 Feb 2026 — Se l'utente ha attivato l'OTP e il device non è "trusted",
      // il backend risponde con { needs_otp: true, challenge_id, email_masked, expires_in_seconds }.
      // In quel caso NON impostiamo user: il flusso continua sulla pagina di verifica OTP.
      if (data?.needs_otp) {
        return { needs_otp: true, challenge_id: data.challenge_id, email_masked: data.email_masked, expires_in_seconds: data.expires_in_seconds };
      }
      if (data?.access_token) localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("session_started_at", String(Date.now()));
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return data;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  // 🆕 Feb 2026 — Verifica il codice OTP inviato al login. Se `remember_device`=true,
  // il server salva un cookie httpOnly `device_trust` valido 30 giorni.
  const verifyOtp = useCallback(async ({ challenge_id, otp, remember_device, device_label }) => {
    setError("");
    try {
      const { data } = await api.post("/auth/verify-otp", {
        challenge_id, otp, remember_device: !!remember_device, device_label: device_label || null,
      });
      if (data?.access_token) localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("session_started_at", String(Date.now()));
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return data;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Logout request failed:", err);
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("session_started_at");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, error, login, verifyOtp, logout, refresh }),
    [user, error, login, verifyOtp, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
